// ============================================================================
// OCCUPANCY DAY CELL — one day of free inventory
// ============================================================================
// Shared by the rolling strip and the month calendar, so the two are literally
// the same cell at two densities. A second copy would drift: the strip would
// keep the oversold treatment and the calendar would quietly lose it, or the
// free-% bar would mean one thing in one place and another elsewhere.
//
// Leads with FREE ROOMS and FREE %, never occupancy — an occupancy-first cell
// reads "83% — healthy" on exactly the day that needs selling.
//
// One unit caution baked into the layout: the hero block is ROOMS, the footer
// line is BOOKINGS (a group booking is one arrival across five rooms). They are
// visually separated so they are never read as one arithmetic.
//
// Design: crs-doc/CRS-Booking-Worksheet-Day-View-Design.md §5.1.

import React, { useState } from 'react';
import type { OccupancyHorizonDay } from 'api/types/availability.types';
import { getAvailColor } from './availability.colors';
import { parseDayKey, todayKey } from 'utils/calendarDate';

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface OccupancyDayCellProps {
  day: OccupancyHorizonDay;
  selected: boolean;
  onSelect: (isoDate: string) => void;
  /**
   * 'strip' — fixed-width cell for the horizontal rail.
   * 'calendar' — fills its grid track and shows the day number only.
   */
  variant?: 'strip' | 'calendar';
}

const OccupancyDayCell: React.FC<OccupancyDayCellProps> = ({
  day: d, selected, onSelect, variant = 'strip',
}) => {
  const [hovered, setHovered] = useState(false);

  const colour = getAvailColor(d.availableRooms, d.totalRooms, d.oversoldRooms);
  const dt = parseDayKey(d.date);
  const isToday = d.date === todayKey();
  const soldOut = d.availableRooms <= 0 && d.oversoldRooms <= 0;
  const isCalendar = variant === 'calendar';

  return (
    <div style={{ position: 'relative', flex: isCalendar ? undefined : '0 0 auto' }}>
      <button
        type="button"
        onClick={() => onSelect(d.date)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        aria-label={
          `${dt.toDateString()}: ${d.availableRooms} of ${d.totalRooms} rooms free ` +
          `(${d.freePercent}%), ${d.arrivingCount} arriving, ` +
          `${d.stayingOverCount} staying, ${d.departingCount} departing`
        }
        style={{
          width: isCalendar ? '100%' : '5.4rem',
          minHeight: isCalendar ? '4.6rem' : undefined,
          textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
          padding: selected ? '0.3rem 0.35rem' : '0.35rem 0.4rem',
          borderRadius: '0.5rem',
          background: 'var(--bg-primary)',
          border: selected ? '2px solid var(--primary)' : '1px solid var(--border-color)',
          boxShadow: selected ? '0 0 0 3px rgba(37,99,235,0.12)' : 'none',
        }}
      >
        <div style={{
          fontSize: '0.65rem', fontWeight: 600,
          color: isToday ? 'var(--primary)' : 'var(--text-secondary)',
          whiteSpace: 'nowrap',
        }}>
          {isCalendar ? dt.getDate() : `${DAY_ABBR[dt.getDay()]} ${dt.getDate()}`}
          {isToday && (
            <span style={{ fontSize: '0.55rem', fontWeight: 800, marginLeft: '0.15rem' }}>•</span>
          )}
        </div>

        {/* Hero: free rooms of total. ROOMS, not bookings. */}
        <div style={{
          fontSize: '1.05rem', fontWeight: 800, lineHeight: 1.1,
          color: colour, fontVariantNumeric: 'tabular-nums',
        }}>
          {d.availableRooms}
          <span style={{ fontSize: '0.6rem', fontWeight: 600, color: 'var(--text-muted)' }}>
            {' '}/{d.totalRooms}
          </span>
        </div>

        {/* The management metric. */}
        <div style={{
          fontSize: '0.62rem', fontWeight: 700, color: colour,
          fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
        }}>
          {d.oversoldRooms > 0
            ? `OVERSOLD ${d.oversoldRooms}`
            : soldOut ? 'SOLD OUT' : `${d.freePercent}% free`}
        </div>

        {/* Bar length IS free % — left-aligned so lengths compare across cells. */}
        <div style={{
          height: '4px', borderRadius: '2px', margin: '0.2rem 0 0.15rem',
          background: 'var(--bg-secondary)', overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', borderRadius: '2px', background: colour,
            width: `${Math.max(0, Math.min(100, d.freePercent))}%`,
          }} />
        </div>

        {/* Footer: BOOKINGS. Kept visually apart from the rooms above. */}
        <div style={{
          fontSize: '0.58rem', color: 'var(--text-muted)',
          fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
        }}>
          ↑{d.arrivingCount} · {d.stayingOverCount} · ↓{d.departingCount}
        </div>
      </button>

      {hovered && <OccupancyHoverCard day={d} />}
    </div>
  );
};

/**
 * Per-category breakdown. Holds are named explicitly because they are excluded
 * from "free": quoting a held room and then losing it costs more than briefly
 * under-quoting. Occupancy % lives here rather than on the cell.
 */
export const OccupancyHoverCard: React.FC<{ day: OccupancyHorizonDay }> = ({ day }) => {
  const row = (k: string, v: React.ReactNode, muted = false) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: '0.75rem',
      fontSize: '0.68rem', color: muted ? 'var(--text-muted)' : 'var(--text-primary)',
    }}>
      <span>{k}</span>
      <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{v}</span>
    </div>
  );

  return (
    <div
      role="tooltip"
      className="action-menu-dropdown"
      style={{
        position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40,
        minWidth: '13rem', padding: '0.5rem 0.6rem', borderRadius: '0.5rem',
        border: '1px solid var(--border-color)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        display: 'flex', flexDirection: 'column', gap: '0.15rem',
      }}
    >
      <div style={{ fontSize: '0.7rem', fontWeight: 800, marginBottom: '0.2rem' }}>
        {parseDayKey(day.date).toLocaleDateString('en-IN', {
          weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
        })}{' '}
        <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>
          — {day.totalRooms} rooms
        </span>
      </div>

      {row('Free', `${day.availableRooms}  (${day.freePercent}%)`)}
      {day.oversoldRooms > 0 && row('Oversold', day.oversoldRooms)}
      {day.holdRooms > 0 && row('On hold — not counted free', day.holdRooms, true)}
      {day.pendingApprovalRooms > 0 && row('Awaiting approval', day.pendingApprovalRooms, true)}
      {row('Confirmed', day.confirmedRooms, true)}
      {row('In-house', day.inHouseRooms, true)}
      {(day.blockedRooms > 0 || day.oooRooms > 0) &&
        row('Blocked / OOO', `${day.blockedRooms} / ${day.oooRooms}`, true)}
      {row('Occupancy', `${day.occupancyPercent}%`, true)}

      <div style={{ borderTop: '1px dashed var(--border-color)', margin: '0.3rem 0 0.25rem' }} />

      {day.unsoldByCategory.length > 0 ? (
        <>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
            Unsold by category
          </div>
          <div style={{ fontSize: '0.68rem' }}>
            {day.unsoldByCategory.map((c) => `${c.availableRooms} ${c.categoryName}`).join(' · ')}
          </div>
        </>
      ) : (
        <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
          Nothing left to sell on this date.
        </div>
      )}

      <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
        ↑{day.arrivingCount} arriving · {day.stayingOverCount} staying · ↓{day.departingCount} departing
        <span style={{ opacity: 0.8 }}> (bookings)</span>
      </div>
    </div>
  );
};

export default OccupancyDayCell;
